import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  useUpdateEcommerceProductMutation,
  useFetchEcommerceProductByIdMutation,
} from "../../../features/tenant/ecommerceProduct/ecommerceProductApi";
import { useFetchEcommerceTagsMutation } from "../../../features/tenant/ecommerceTags/ecommerceTagsApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import MultiSelectComponent from "../../../components/Tenant/MultiSelectComponent/MultiSelectComponent";
import { FiX, FiPlus } from "react-icons/fi";

const EditEcommerceProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenantName = vendorData.tenant_name;
  const tenant_id = vendorData.tenant_id;
  const token = sessionStorage.getItem("access_token");

  const [formData, setFormData] = useState({
    mrp: "", tags: [], image: [], product_name: "", article_no: "",
  });

  const [availableTags, setAvailableTags] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [fetchProduct, { data: productData, isLoading: isProductLoading }] = useFetchEcommerceProductByIdMutation();
  const [updateProduct, { data: updateData, isLoading: isUpdating, error: updateError }] = useUpdateEcommerceProductMutation();
  const [fetchTags, { data: tagsData }] = useFetchEcommerceTagsMutation();
  const [uploadSingleFile, { isLoading: isUploading }] = useUploadSingleFileMutation();

  useEffect(() => {
    fetchProduct({ slug: tenant_id, token, id });
    fetchTags({ slug: tenant_id, token });
  }, [id]);

  useEffect(() => {
    if (productData?.body?.data?.length > 0 && availableTags.length > 0 && !isInitialized) {
      const product = productData.body.data[0];
      const productTags = product.tags || [];
      const selectedTags = availableTags.filter(
        (t) => productTags.includes(t.id?.toString()) || productTags.includes(t.id)
      );

      setFormData({
        mrp: product.mrp || "",
        tags: selectedTags,
        image: product.image || [],
        product_name: product.product_name || "",
        article_no: product.article_no || "",
      });
      setIsInitialized(true);
    }
  }, [productData, availableTags, isInitialized]);

  useEffect(() => {
    if (tagsData?.body) {
      const d = Array.isArray(tagsData.body) ? tagsData.body : (tagsData.body.data || []);
      setAvailableTags(d.map((t) => ({ id: t.id, name: t.name })));
    }
  }, [tagsData]);

  useEffect(() => {
    if (updateData) {
      toast.success("Product updated successfully");
      navigate(`/genefied/${tenantName}/ecommerce-products`);
    }
  }, [updateData]);

  useEffect(() => {
    if (updateError) {
      toast.error(updateError.data?.message || "Failed to update product");
    }
  }, [updateError]);

  // ── Image Handlers ──────────────────────────────────────────────────────

  const handleFileUpload = async (e) => {
    for (const file of Array.from(e.target.files)) {
      const fd = new FormData();
      fd.append("image", file);
      try {
        const r = await uploadSingleFile({ token, body: fd }).unwrap();
        if (r?.body?.fileLink) {
          setFormData((p) => ({ ...p, image: [...p.image, r.body.fileLink] }));
        }
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    e.target.value = null;
  };

  const handleRemoveImage = (i) =>
    setFormData((p) => ({ ...p, image: p.image.filter((_, idx) => idx !== i) }));

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();
    updateProduct({
      slug: tenant_id,
      token,
      id: id.toString(),
      mrp: parseFloat(formData.mrp) || 0,
      tags: formData.tags?.map((t) => t.id) || [],
      image: formData.image || [],
    });
  };

  if (isProductLoading) return <div className="p-8 text-center text-gray-500">Loading product...</div>;

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Update Product</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          {formData.product_name} {formData.article_no && `(${formData.article_no})`}
        </p>
        <button onClick={() => navigate(`/genefied/${tenantName}/ecommerce-products`)}
          className="text-blue-600 hover:text-blue-800 mt-2 text-sm">
          ← Back to Products
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-6">

          {/* Product Name (Read-Only) */}
          <div className="max-w-md">
            <InputComponent name="Product Name" type="text" value={formData.product_name}
              disabled={true} placeholder="Product Name" />
          </div>

          {/* Article No (Read-Only) */}
          <div className="max-w-xs">
            <InputComponent name="Article No" type="text" value={formData.article_no}
              disabled={true} placeholder="Article No" />
          </div>

          {/* Default MRP */}
          <div className="max-w-xs">
            <InputComponent name="MRP" type="number" value={formData.mrp}
              setValue={(v) => setFormData({ ...formData, mrp: v })}
              placeholder="Enter MRP" required />
          </div>

          {/* Tags */}
          <div>
            <MultiSelectComponent title="Tags" dataArr={availableTags} selected={formData.tags}
              setSelected={(v) => setFormData({ ...formData, tags: v })} />
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <FiPlus className="w-8 h-8 mb-1 text-gray-500" />
              <p className="text-sm text-gray-500">Click to upload images</p>
              <input type="file" className="hidden" multiple onChange={handleFileUpload}
                disabled={isUploading} accept="image/*" />
            </label>
            {isUploading && <p className="text-xs text-blue-500 mt-2">Uploading...</p>}
            <div className="grid grid-cols-4 gap-4 mt-4">
              {formData.image.map((img, i) => (
                <div key={i} className="relative">
                  <img src={img} alt="Product" className="w-full h-24 object-cover rounded" />
                  <button type="button" onClick={() => handleRemoveImage(i)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                    <FiX size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4 pt-2 border-t border-gray-100">
            <button type="button" onClick={() => navigate(`/genefied/${tenantName}/ecommerce-products`)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded">
              Cancel
            </button>
            <button type="submit" disabled={isUpdating}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded disabled:opacity-50">
              {isUpdating ? "Updating..." : "Update Product"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditEcommerceProduct;
